import { conditionalRender } from '@repo/toolkit/conditional-render'
import InnerBox from '@repo/ui/inner-box'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import { getFranceDepartmentsList } from '../../../api/countries/get-france-departments-list'
import { getNationalitiesList } from '../../../api/countries/get-nationalities-list'
import { getHowDidYouHearAboutUs } from '../../../api/how-did-you-hear-about-us/get-how-did-you-hear-about-us'
import useTranslate from '../../../lib/hooks/use-translate'
import { GenderEnum, genderEnumMapping } from '../../../lib/types/enums/common/gender.enum'
import {
  MaritalStatusEnum,
  maritalStatusEnumMapping
} from '../../../lib/types/enums/staff-application/marital-status.enum'
import { CountryKey, getCountryLabel } from '../../../lib/types/models/country/country-list'
import { StaffApplicationInput } from '../../../lib/types/models/staff-application/staff-application-input'
import {
  getSelectedEnumOption,
  getSelectedOption
} from '../../../lib/utils/functions/misc.functions'
import ControlledSwitch from '../../elements/controlled-switch'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import LoadingScreen from '../../elements/loading/loading-screen'

const StaffApplicationFormPersonalInformation = () => {
  const { t, currentLanguage } = useTranslate()
  const form = useFormContext<StaffApplicationInput>()
  const genderMapping = genderEnumMapping()

  const { data: howDidYouHearAboutUsOptions, isPending } = useQuery({
    queryKey: ['howDidYouHearAboutUsOptions'],
    queryFn: () => getHowDidYouHearAboutUs()
  })

  const { data: countryList, isPending: isLoadingCountries } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const { data: nationalitiesList, isPending: isLoadingNationalities } = useQuery({
    queryKey: ['nationalities-list'],
    queryFn: getNationalitiesList
  })

  const { data: franceDepartmentsList, isPending: isLoadingFranceDepartments } = useQuery({
    queryKey: ['france-departments-list'],
    queryFn: getFranceDepartmentsList
  })

  if (isPending || isLoadingFranceDepartments || isLoadingNationalities || isLoadingCountries)
    return <LoadingScreen />

  return (
    <InnerBox title="Informations personnelles">
      <div className="flex gap-4 flex-col w-full">
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Genre"
                    options={Object.values(GenderEnum).map((status) => ({
                      label: genderMapping[status],
                      value: status
                    }))}
                    selectedOption={getSelectedEnumOption(field.value, genderMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="text" label="Prénom" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="text" label="NOM" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledPhoneInput
                    {...field}
                    type="text"
                    label="Numéro de téléphone"
                    defaultCountry="FR"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput {...field} type="email" label="Email" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="address1"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledAdressInput
                  {...field}
                  label="Adresse"
                  onDetailsChange={(placeDetails) => {
                    form.setValue('address1', placeDetails.address, { shouldValidate: true })
                    form.setValue('postalCode', placeDetails.postalCode, { shouldValidate: true })
                    form.setValue('city', placeDetails.city, { shouldValidate: true })
                    form.setValue('country', placeDetails.country, { shouldValidate: true })
                    form.trigger(['address1', 'postalCode', 'city', 'country'])
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address2"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput {...field} type="text" label="Complément d'adresse" />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput {...field} type="text" label="Ville" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="postalCode"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput {...field} type="text" label="Code postal" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput {...field} type="text" label="Pays" />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="nationality"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Nationalité"
                    searchable
                    options={nationalitiesList?.map((nationality) => ({
                      label: nationality.label,
                      value: nationality.code
                    }))}
                    selectedOption={getSelectedOption(
                      field.value,
                      nationalitiesList?.find((national) => national.code === field.value)?.label
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="partOfTheEuropeanUnion"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSwitch {...field} label="Fait partie de l'Union Européenne" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="birthDate"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    type="birthdate"
                    label="Date de naissance"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="birthPlace"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    label={'Pays'}
                    options={countryList?.data.map((country) => ({
                      label: getCountryLabel(currentLanguage, country) ?? '',
                      value: country.key
                    }))}
                    onChange={(value) => {
                      form.setValue('birthPlace', value, { shouldValidate: true })
                      if (value !== CountryKey.FRA.toString())
                        form.setValue('birthDepartment', null, { shouldValidate: true })
                    }}
                    selectedOption={conditionalRender(!!field?.value, {
                      value: field.value,
                      label:
                        getCountryLabel(
                          currentLanguage,
                          countryList?.data.find((country) => country.key === field.value)
                        ) ?? ''
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="birthCity"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput {...field} type="text" label="Ville de naissance" />
                </FormControl>
              </FormItem>
            )}
          />
          {form.watch('birthPlace') === CountryKey.FRA.toString() && (
            <FormField
              control={form.control}
              name="birthDepartment"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      searchable
                      label="Département de naissance"
                      options={franceDepartmentsList?.map((department) => ({
                        label: department,
                        value: department
                      }))}
                      selectedOption={getSelectedOption(field.value, field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
        </div>
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="maritalStatus"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Situation familiale"
                    options={Object.values(MaritalStatusEnum).map((status) => ({
                      label: t(maritalStatusEnumMapping[status]),
                      value: status
                    }))}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      maritalStatusEnumMapping,
                      t
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="numberOfChildren"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    isControllable
                    allowNegative={false}
                    label="Nombre d'enfants"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="hasComplemenaryInsurance"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSwitch {...field} label="A-t-il une assurance complémentaire?" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="complementaryInsuranceName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="text"
                    disabled={!form.watch('hasComplemenaryInsurance')}
                    label="Assurance complémentaire"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3 w-full">
          <FormField
            control={form.control}
            name="socialSecurityNumber"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput {...field} type="text" label="Numéro de sécurité sociale" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="howDidYouHearAboutUsOptionId"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Comment avez-vous connu LEC ?"
                    options={howDidYouHearAboutUsOptions?.data.map((option) => ({
                      label: option.name,
                      value: option.id
                    }))}
                    selectedOption={getSelectedOption(
                      field.value,
                      howDidYouHearAboutUsOptions?.data.find((option) => option.id === field.value)
                        ?.name || ''
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </InnerBox>
  )
}

export default StaffApplicationFormPersonalInformation
